import { useCallback, useEffect, useMemo, useState } from "react";

/**
 * v6 Fase F3 — Reusable accordion/collapse state hook.
 *
 * Maintains a `{ [id]: true|false }` collapsed map persisted to localStorage
 * under `storageKey`. Each entry's default value falls back to
 * `defaultCollapsed` when the key is missing from cache.
 *
 *   const { collapsed, isCollapsed, toggle, expandAll, collapseAll } =
 *     useCollapseMap("emma:listCollapsed:clients", clients, true);
 *
 *   <button onClick={() => toggle(client.id)}>...</button>
 *   {!isCollapsed(client.id) && <DetailPanel />}
 *
 * @param {string}  storageKey         — full localStorage key (caller scopes it)
 * @param {Array<{id:string}>} items   — list of items (used by expand/collapseAll)
 * @param {boolean} defaultCollapsed   — true = collapsed by default (F3 standard)
 */
export default function useCollapseMap(storageKey, items, defaultCollapsed = true) {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch { return {}; }
  });

  // Persist on every change (cheap — only fires on user interaction).
  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(collapsed)); }
    catch { /* quota/unavailable */ }
  }, [storageKey, collapsed]);

  const isCollapsed = useCallback((id) => {
    const v = collapsed[id];
    return v === undefined ? defaultCollapsed : !!v;
  }, [collapsed, defaultCollapsed]);

  const toggle = useCallback((id) => {
    setCollapsed((s) => ({ ...s, [id]: s[id] === undefined ? !defaultCollapsed : !s[id] }));
  }, [defaultCollapsed]);

  const expandAll = useCallback(() => {
    const next = {};
    items.forEach((it) => { next[it.id] = false; });
    setCollapsed(next);
  }, [items]);

  const collapseAll = useCallback(() => {
    const next = {};
    items.forEach((it) => { next[it.id] = true; });
    setCollapsed(next);
  }, [items]);

  const allCollapsed = useMemo(
    () => items.length > 0 && items.every((it) => isCollapsed(it.id)),
    [items, isCollapsed],
  );

  return { collapsed, isCollapsed, toggle, expandAll, collapseAll, allCollapsed };
}
